import type { OrgInvitationSummary } from '@nadoutong/contracts';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useRevokeOrgInvitation } from '../../api/useOrgs.ts';
import { InviteLinkModal, InviteMemberModal } from '../../components/InviteMemberDialog.tsx';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, ConfirmDialog } from '../../ui/index.ts';
import { formatDate } from './formatDate.ts';

/**
 * Pending invitations — the `invitations` tab of `/organization`, and
 * (in `compact` form) a three-row preview under the member directory.
 *
 * What this gained from console-web's `/team` page (epic #1 PR-4) is
 * revoke: the list used to be read-only here, so an invitation sent to
 * the wrong address could only be waited out until it expired.
 *
 * Revoke always runs through a danger `ConfirmDialog` — the source page
 * records a fixed bug where a single click mutated — and reports its
 * failure inline in that dialog, so a rejected revoke does not read as
 * a silent no-op. The panel owns the mutation because the page has no
 * other use for it.
 *
 * Create is here for the same reason console-web puts "invite member" on
 * the page that manages members: a list of invitations with no way to
 * add one is a dead end, and the entry point sitting on `/settings` was
 * discoverable only to someone who already knew. The composer is the
 * shared `InviteMemberModal`, so both pages issue the same POST and, via
 * the one invalidated query key, show the result in the same breath.
 *
 * Permission gating: without `org:invitations:manage` neither control is
 * rendered, so no POST or DELETE can be issued from this view. The empty
 * state still names who can invite instead of stopping at "none".
 */

interface Props {
  readonly orgId: string | undefined;
  readonly invitations: readonly OrgInvitationSummary[];
  readonly canManageInvitations: boolean;
  readonly compact?: boolean;
}

export function InvitationsPanel({
  orgId,
  invitations,
  canManageInvitations,
  compact = false,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const revokeM = useRevokeOrgInvitation();
  const [revokeTarget, setRevokeTarget] = useState<OrgInvitationSummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);

  // Create and revoke are the same permission (`org:invitations:manage`)
  // and both need an org to act on, so one gate answers for both.
  const canManage = canManageInvitations && orgId !== undefined;
  const columns = canManage
    ? 'grid-cols-[minmax(0,1fr)_7rem_10rem_6rem]'
    : 'grid-cols-[minmax(0,1fr)_7rem_10rem]';

  async function handleRevoke(): Promise<void> {
    if (!canManage || orgId === undefined || revokeTarget === null) {
      return;
    }
    setRevokeError(null);
    try {
      await revokeM.mutateAsync({ orgId, invitationId: revokeTarget.id });
      setRevokeTarget(null);
      toast.success(t('organization.invitations.toast.revoked'));
    } catch (err) {
      setRevokeError(localizeCaughtError(err, t, 'organization.invitations.revoke_error'));
    }
  }

  return (
    <div
      data-testid="organization-invitations"
      className="rounded-lg border border-line bg-card p-4"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-body font-medium text-ink">{t('organization.invitations.title')}</h3>
          {compact ? null : (
            <p className="text-caption text-ink-low">{t('organization.invitations.subtitle')}</p>
          )}
        </div>
        {canManage ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setInviteOpen(true)}
            data-testid="organization-invitations-invite"
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('organization.invitations.invite')}
          </Button>
        ) : null}
      </header>
      {invitations.length === 0 ? (
        <div className="mt-4 flex flex-col gap-1" data-testid="organization-invitations-empty">
          <p className="m-0 text-body text-ink-low">{t('organization.invitations.empty')}</p>
          <p className="m-0 text-caption text-ink-low">
            {t(
              canManage
                ? 'organization.invitations.empty_hint'
                : 'organization.invitations.empty_hint_readonly',
            )}
          </p>
        </div>
      ) : (
        <div className="mt-4 divide-y divide-line-soft">
          {invitations.slice(0, compact ? 3 : invitations.length).map((invitation) => (
            <div
              key={invitation.id}
              data-testid={`organization-invitation-row-${invitation.id}`}
              className={`grid ${columns} items-center gap-4 py-3 text-body`}
            >
              <span className="min-w-0 truncate font-mono text-ink">{invitation.email}</span>
              {/* Same catalog entry the member directory's role column
                  uses — an invitation is a pending member, so the two
                  surfaces must name the role with the same word. */}
              <span className="text-ink-mid">
                {t(
                  invitation.role === 'admin'
                    ? 'organization.role.admin'
                    : invitation.role === 'finance'
                      ? 'organization.role.finance'
                      : 'organization.role.member',
                )}
              </span>
              <span className="truncate text-right font-mono text-caption text-ink-low">
                {formatDate(invitation.expiresAt)}
              </span>
              {canManage ? (
                <span className="flex justify-end">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={revokeM.isPending}
                    onClick={() => {
                      setRevokeError(null);
                      setRevokeTarget(invitation);
                    }}
                    data-testid={`organization-invitation-revoke-${invitation.id}`}
                  >
                    {t('organization.invitations.revoke')}
                  </Button>
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {revokeTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !revokeM.isPending) {
              setRevokeTarget(null);
              setRevokeError(null);
            }
          }}
          tone="danger"
          title={t('organization.invitations.revoke_title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('organization.invitations.revoke_body', { email: revokeTarget.email })}
              </p>
              {revokeError !== null ? (
                <p
                  role="alert"
                  data-testid="organization-invitations-revoke-error"
                  className="m-0 text-caption text-bad"
                >
                  {revokeError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('organization.invitations.revoke_confirm')}
          cancelLabel={t('organization.action.cancel')}
          closeLabel={t('organization.invitations.revoke_close')}
          loading={revokeM.isPending}
          testId="organization-invitation-revoke-dialog"
          onConfirm={() => {
            void handleRevoke();
          }}
        />
      ) : null}

      {inviteOpen && canManage && orgId !== undefined ? (
        <InviteMemberModal
          orgId={orgId}
          onClose={() => setInviteOpen(false)}
          onCreated={(url) => {
            setInviteOpen(false);
            setLastInviteUrl(url);
          }}
        />
      ) : null}

      {lastInviteUrl !== null ? (
        <InviteLinkModal url={lastInviteUrl} onClose={() => setLastInviteUrl(null)} />
      ) : null}
    </div>
  );
}
